/**
 * Typed HTTP request helper for the Runhuman customer API.
 *
 * The single place where auth headers, query building, error parsing, and
 * network-failure retries live — every DirectJobClient method funnels
 * through requestJson(), so the request/error/retry contract is defined
 * (and tested) exactly once.
 */

/** Error body shape the Runhuman API returns on non-2xx responses. */
interface ApiErrorBody {
  error?: string;
  message?: string;
  code?: string;
  link?: string;
  reason?: string;
}

/** Non-2xx API response, carrying the parsed body fields the MCP tools key off. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly link?: string;
  readonly reason?: string;

  constructor(message: string, fields: { status: number; code?: string; link?: string; reason?: string }) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = fields.status;
    this.code = fields.code;
    this.link = fields.link;
    this.reason = fields.reason;
  }
}

/**
 * Connection-level failures worth retrying: the request never produced an
 * HTTP response, so re-sending is safe. Keyed on the structured `cause.code`
 * undici attaches — never on message text. `UND_ERR_SOCKET` is undici's
 * "other side closed" (a stale keep-alive socket dropped mid-poll).
 */
const RETRYABLE_CAUSE_CODES: ReadonlySet<string> = new Set([
  'ECONNRESET',
  'ENOTFOUND',
  'UND_ERR_SOCKET',
]);

const RETRY_DELAY_MS = 1000;

export interface ApiRequestConfig {
  /** Base URL of the Runhuman API */
  apiUrl: string;
  /** Personal Access Token (rh_pat_xxx) */
  apiKey: string;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Record<string, string>;
  /** JSON-serialized into the request body. */
  body?: unknown;
  /** Extra headers merged over the defaults (e.g. `Connection: close` on polling paths). */
  headers?: Record<string, string>;
  /** Overrides the thrown message when the API answers 404. */
  notFoundMessage?: string;
  /** Retries on RETRYABLE_CAUSE_CODES connection failures, RETRY_DELAY_MS apart. Default 0. */
  retries?: number;
  /** Aborts the request after this many milliseconds. Default: no timeout. */
  timeoutMs?: number;
}

export async function requestJson<T>(
  config: ApiRequestConfig,
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = 'GET', retries = 0 } = options;
  const url = buildUrl(config.apiUrl, path, options.query);

  for (let attempt = 0; ; attempt++) {
    try {
      return await performRequest<T>(config, url, options);
    } catch (error) {
      if (error instanceof ApiRequestError) throw error;
      if (!isRetryableConnectionError(error) || attempt >= retries) {
        throw describeNetworkFailure(method, path, error);
      }
      console.error(`[DirectJobClient] ${method} ${path} failed (attempt ${attempt + 1}/${retries + 1}), retrying...`);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }
}

function buildUrl(apiUrl: string, path: string, query?: Record<string, string>): string {
  const base = `${apiUrl}${path}`;
  if (!query) return base;
  const queryString = new URLSearchParams(query).toString();
  return queryString ? `${base}?${queryString}` : base;
}

async function performRequest<T>(config: ApiRequestConfig, url: string, options: ApiRequestOptions): Promise<T> {
  const { method = 'GET', body, headers, timeoutMs } = options;

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${config.apiKey}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: timeoutMs !== undefined ? AbortSignal.timeout(timeoutMs) : undefined,
  });

  if (!response.ok) {
    throw await buildApiError(response, options.notFoundMessage);
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function buildApiError(response: Response, notFoundMessage?: string): Promise<ApiRequestError> {
  const raw = await response.text();
  const body = parseErrorBody(raw);
  const message =
    response.status === 404 && notFoundMessage !== undefined
      ? notFoundMessage
      : body?.error ?? body?.message ?? (body === undefined && raw ? raw : response.statusText);

  return new ApiRequestError(message, {
    status: response.status,
    code: body?.code,
    link: body?.link,
    reason: body?.reason,
  });
}

/** Error bodies are external data: proxies answer with HTML/plain text, so `undefined` = not JSON. */
function parseErrorBody(raw: string): ApiErrorBody | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : undefined;
  } catch {
    return undefined;
  }
}

function isRetryableConnectionError(error: unknown): boolean {
  if (!(error instanceof Error) || !(error.cause instanceof Error)) return false;
  const { code } = error.cause as Error & { code?: unknown };
  return typeof code === 'string' && RETRYABLE_CAUSE_CODES.has(code);
}

function describeNetworkFailure(method: string, path: string, error: unknown): Error {
  const parts = [error instanceof Error ? error.message : String(error)];
  if (error instanceof Error && error.cause instanceof Error) {
    const { code } = error.cause as Error & { code?: string };
    parts.push(`cause: ${error.cause.message}${code !== undefined ? ` (${code})` : ''}`);
  }
  return new Error(`${method} ${path} failed: ${parts.join('; ')}`, { cause: error });
}
